import { DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  AgentProfileDto,
  AgentProfileId,
  AgentServiceOffering,
  AgentSpecialty,
  AgentUserPublicDto,
  FRENCH_DEPARTMENTS_MAP,
  FRENCH_REGIONS,
  FrenchDepartment,
  UserApplications,
} from '@workspace/data-types';
import { ApplicationStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, finalize, of, tap } from 'rxjs';
import { AgentCardDisplayComponent } from '../../../../agent-card';

export type AgentProfileDisplayMode =
  | 'all'
  | 'completion'
  | 'responsiveness'
  | 'network'
  | 'specialties'
  | 'services'
  | 'activeCard'
  | 'cities'
  | 'zones'
  | 'bio'
  | 'expiredCards'
  | 'socialLinks'
  | 'officeHours';

@Component({
  selector: 'etxe-agent-profile-display',
  templateUrl: './agent-profile-display.component.html',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatTooltipModule,
    AgentCardDisplayComponent,
    DatePipe,
    DecimalPipe,
    UpperCasePipe,
    RouterModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentProfileDisplayComponent {
  agentProfileId = input<AgentProfileId | null | undefined>(null);
  profileData = input<AgentProfileDto | null>(null);
  showLinkToAgentPage = input<boolean>(false);
  showMode = input<AgentProfileDisplayMode>('all');
  agentProfile = signal<AgentProfileDto | null>(null);
  isLoading = signal<boolean>(false);

  private readonly api = inject(ApiService);
  private readonly appStore = inject(ApplicationStore);
  private readonly destroyRef = inject(DestroyRef);

  // Direct signals to BDD subdocuments (strict new data model, no backward compatibility)
  readonly cards = computed(() => this.agentProfile()?.cards);
  readonly activeCard = computed(() => this.cards()?.activeCard ?? null);
  readonly expiredCards = computed(() => this.cards()?.expiredCards ?? []);

  readonly intervention = computed(() => this.agentProfile()?.intervention);
  readonly primaryRegion = computed(() => this.intervention()?.primaryRegion ?? null);
  readonly interventionZones = computed(() => this.intervention()?.zones ?? []);
  readonly preferredCities = computed(() => this.intervention()?.preferredCities ?? []);

  readonly activity = computed(() => this.agentProfile()?.activity);
  readonly network = computed(() => this.activity()?.network ?? null);
  readonly specialties = computed(() => this.activity()?.specialties ?? []);
  readonly bio = computed(() => this.activity()?.bio ?? '');
  readonly services = computed(() => this.activity()?.services ?? []);
  readonly socialLinks = computed(() => this.activity()?.socialLinks ?? null);
  readonly feeScaleUrl = computed(() => this.activity()?.feeScaleUrl ?? null);
  readonly averageCommissionRate = computed(() => this.activity()?.averageCommissionRate ?? null);

  readonly agentUser = computed<AgentUserPublicDto | null>(() => {
    const u = this.agentProfile()?.user;
    return typeof u === 'object' && u !== null ? (u as AgentUserPublicDto) : null;
  });

  readonly responsiveness = computed(() => {
    return this.agentUser()?.responsiveness ?? null;
  });
  readonly isHighlyResponsive = computed(() => this.responsiveness()?.isHighlyResponsive ?? false);
  readonly responseRatePercent = computed(() => this.responsiveness()?.responseRatePercent ?? null);
  readonly averageResponseTimeHours = computed(() => this.responsiveness()?.averageResponseTimeHours ?? null);
  readonly expiredActionsCount = computed(() => this.responsiveness()?.expiredActionsCount ?? 0);
  readonly formattedResponseTime = computed(() => {
    const hours = this.averageResponseTimeHours();
    if (hours == null) return null;
    if (hours < 1) return '< 1 h';
    if (hours === 1) return '1 h';
    const rounded = Math.round(hours * 10) / 10;
    return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1).replace('.', ',')} h`;
  });

  readonly work = computed(() => this.agentProfile()?.work);
  readonly officeAddress = computed(() => this.work()?.officeAddress ?? null);
  readonly openingHours = computed(() => this.work()?.openingHours ?? null);

  readonly settings = computed(() => this.agentProfile()?.settings ?? null);
  readonly visibility = computed(() => this.settings()?.visibility ?? null);
  readonly notifications = computed(() => this.settings()?.notifications ?? null);
  readonly contactInfos = computed(() => this.settings()?.contactInfos ?? null);

  readonly specialtyMeta: Record<string, { label: string; icon: string }> = {
    [AgentSpecialty.APARTMENT]: { label: 'Appartements', icon: 'apartment' },
    [AgentSpecialty.HOUSE]: { label: 'Maisons & Villas', icon: 'home' },
    [AgentSpecialty.PRESTIGE]: { label: 'Prestige & Luxe', icon: 'diamond' },
    [AgentSpecialty.BUILDING]: { label: 'Immeubles de rapport', icon: 'domain' },
    [AgentSpecialty.LAND]: { label: 'Terrains', icon: 'landscape' },
    [AgentSpecialty.COMMERCIAL]: { label: 'Commerces & Pro', icon: 'storefront' },
  };

  readonly serviceMeta: Record<string, { label: string; icon: string }> = {
    [AgentServiceOffering.PHOTO_PRO]: { label: 'Photographie professionnelle', icon: 'photo_camera' },
    [AgentServiceOffering.VIRTUAL_TOUR]: { label: 'Visite virtuelle 3D', icon: 'view_in_ar' },
    [AgentServiceOffering.DRONE]: { label: 'Prises de vue par drone', icon: 'flight' },
    [AgentServiceOffering.HOME_STAGING]: { label: 'Home staging & Valorisation', icon: 'auto_awesome' },
    [AgentServiceOffering.FINANCIAL_SCREENING]: {
      label: 'Filtrage financier acquéreurs',
      icon: 'verified_user',
    },
    [AgentServiceOffering.REPORT_24H]: { label: 'Compte-rendu de visite sous 24h', icon: 'schedule' },
    [AgentServiceOffering.PREMIUM_PORTALS]: { label: 'Diffusion portails premium', icon: 'campaign' },
    [AgentServiceOffering.NOTARY_SUPPORT]: { label: 'Accompagnement notarial', icon: 'gavel' },
  };

  profileCompletion = computed(() => {
    const p = this.agentProfile();
    if (!p) return 0;

    let score = this.activeCard() ? 30 : 0; // Base: Card & Identity validated

    if (this.primaryRegion()) score += 10;

    const zones = this.interventionZones();
    const hasDepts = (zones?.[0]?.departments?.length ?? 0) > 0;
    if (hasDepts) score += 20;

    const preferredCities = this.preferredCities();
    if (preferredCities && preferredCities.length > 0) score += 15;

    const network = this.network();
    if (network && network.trim().length > 0) score += 10;

    const specialties = this.specialties();
    if (specialties && specialties.length > 0) score += 10;

    const bio = this.bio();
    if (bio && bio.trim().length > 0) score += 5;

    return Math.min(score, 100);
  });

  readonly completionTip = computed(() => {
    const p = this.agentProfile();
    if (!p) return 'Conseil : Complétez vos informations de profil';

    if (!this.activeCard()) {
      return 'Action requise : Votre carte professionnelle T a expiré ou est absente';
    }

    const bio = this.bio();
    if (!bio || bio.trim().length === 0) {
      return 'Conseil : Rédigez votre biographie pour vous démarquer';
    }

    const network = this.network();
    if (!network || network.trim().length === 0) {
      return 'Conseil : Renseignez votre réseau ou agence';
    }

    const specialties = this.specialties();
    if (!specialties || specialties.length === 0) {
      return 'Conseil : Indiquez vos spécialités immobilières';
    }

    const preferredCities = this.preferredCities();
    if (!preferredCities || preferredCities.length === 0) {
      return 'Conseil : Ajoutez vos villes et communes cibles';
    }

    const zones = this.interventionZones();
    const hasDepts = (zones?.[0]?.departments?.length ?? 0) > 0;
    if (!hasDepts) {
      return "Conseil : Précisez vos départements d'intervention";
    }

    return 'Conseil : Complétez vos informations de profil';
  });

  readonly isDismissed = signal<boolean>(false);
  readonly justReached100 = signal<boolean>(false);
  private initialScore: number | null = null;

  readonly isCompletionVisible = computed(() => {
    if (this.isDismissed()) return false;
    const score = this.profileCompletion();
    if (score < 100) return true;
    return this.justReached100();
  });

  onDismissCompletion(): void {
    this.isDismissed.set(true);
  }

  constructor() {
    effect(() => {
      const p = this.agentProfile();
      if (!p) return;
      const score = this.profileCompletion();
      if (this.initialScore === null) {
        this.initialScore = score;
      } else if (this.initialScore < 100 && score === 100) {
        this.justReached100.set(true);
      }
    });
    effect(() => {
      const data = this.profileData();
      if (data) {
        this.agentProfile.set(data);
        this.isLoading.set(false);
        return;
      }

      const isAdmin = this.appStore.name() === UserApplications.ADMIN;
      const profileId = this.agentProfileId();

      if (isAdmin && !profileId) {
        this.agentProfile.set(null);
        return;
      }

      this.isLoading.set(true);
      const apiCall =
        isAdmin && profileId
          ? this.api.business().etxe().admin().agents().get(profileId)
          : this.api.business().etxe().agent().get();

      apiCall
        .pipe(
          tap((profile) => this.agentProfile.set(profile)),
          catchError((err) => {
            console.error('Failed to load agent profile', err);
            return of(null);
          }),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    });
  }

  getDepartmentLabel(deptCode: string): string {
    const info = FRENCH_DEPARTMENTS_MAP[deptCode as FrenchDepartment];
    if (info) return `${info.name} (${info.code})`;

    const key = Object.keys(FrenchDepartment).find(
      (k) => FrenchDepartment[k as keyof typeof FrenchDepartment] === deptCode,
    );
    if (!key) return deptCode;

    return (
      key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ') + ` (${deptCode})`
    );
  }

  getDepartmentCode(deptCode: string): string {
    const info = FRENCH_DEPARTMENTS_MAP[deptCode as FrenchDepartment];
    return info ? info.code : deptCode;
  }

  getDepartmentNameOnly(deptCode: string): string {
    const info = FRENCH_DEPARTMENTS_MAP[deptCode as FrenchDepartment];
    if (info) return info.name;

    const key = Object.keys(FrenchDepartment).find(
      (k) => FrenchDepartment[k as keyof typeof FrenchDepartment] === deptCode,
    );
    if (!key) return deptCode;

    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getDepartmentsByRegion(departments?: string[]): Array<{
    regionName: string;
    isPrimary: boolean;
    departments: Array<{ code: string; name: string }>;
  }> {
    if (!departments || departments.length === 0) return [];
    const primaryRegion = this.primaryRegion();
    const primaryRegionName = this.getRegionName(primaryRegion);

    const map = new Map<string, Array<{ code: string; name: string }>>();

    for (const dept of departments) {
      const info = FRENCH_DEPARTMENTS_MAP[dept as FrenchDepartment];
      const code = info ? info.code : dept;
      const name = info ? info.name : this.getDepartmentNameOnly(dept);
      const region = info ? this.getRegionName(info.region) : 'Autres départements';

      let list = map.get(region);
      if (!list) {
        list = [];
        map.set(region, list);
      }
      list.push({ code, name });
    }

    return Array.from(map.entries())
      .map(([regionName, depts]) => ({
        regionName,
        isPrimary: !!primaryRegionName && regionName === primaryRegionName,
        departments: depts.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
      }))
      .sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return a.regionName.localeCompare(b.regionName);
      });
  }

  getRegionName(regCode?: string | null): string {
    if (!regCode) return '';
    return FRENCH_REGIONS.find((r) => r.code === regCode)?.name || regCode;
  }

  readonly resolvedAddress = computed(() => {
    const profile = this.agentProfile();
    if (!profile) return null;

    const office = this.officeAddress();
    const hasCustom = Boolean(office?.streetName || office?.city || office?.zipCode);

    if (hasCustom && office) {
      const line1 = [office.streetNumber, office.streetType, office.streetName].filter(Boolean).join(' ');
      const line2 = [office.zipCode, office.city].filter(Boolean).join(' ');
      const complement = office.complement;
      const fullText = [line1, complement, line2].filter(Boolean).join(', ');
      return {
        line1,
        line2,
        complement,
        fullText,
        isCustom: true,
        sourceLabel: 'Adresse de réception / Agence',
      };
    }

    const cardAddr = this.activeCard()?.address;
    if (cardAddr) {
      const line1 = [cardAddr.streetNumber, cardAddr.streetType, cardAddr.streetName]
        .filter(Boolean)
        .join(' ');
      const line2 = [cardAddr.zipCode, cardAddr.city].filter(Boolean).join(' ');
      const complement = cardAddr.complement;
      const fullText = [line1, complement, line2].filter(Boolean).join(', ');
      return {
        line1,
        line2,
        complement,
        fullText,
        isCustom: false,
        sourceLabel: 'Adresse certifiée Carte T (CCI)',
      };
    }

    return null;
  });

  readonly mapsUrl = computed(() => {
    const addr = this.resolvedAddress();
    if (!addr?.fullText) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr.fullText)}`;
  });

  readonly hasSocialLinks = computed(() => {
    const s = this.socialLinks();
    if (!s) return false;
    return Boolean(
      (s.website && s.website.trim().length > 0) ||
      (s.googleReviewUrl && s.googleReviewUrl.trim().length > 0) ||
      (s.opinionSystemUrl && s.opinionSystemUrl.trim().length > 0) ||
      (s.linkedin && s.linkedin.trim().length > 0),
    );
  });

  readonly hasOfficeHours = computed(() => {
    return Boolean(this.resolvedAddress() || this.openingHours());
  });

  readonly weekSchedule = computed(() => {
    const hours = this.openingHours();
    const currentJsDay = new Date().getDay();

    const days: Array<{
      key: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
      label: string;
      jsDay: number;
    }> = [
      { key: 'monday', label: 'Lundi', jsDay: 1 },
      { key: 'tuesday', label: 'Mardi', jsDay: 2 },
      { key: 'wednesday', label: 'Mercredi', jsDay: 3 },
      { key: 'thursday', label: 'Jeudi', jsDay: 4 },
      { key: 'friday', label: 'Vendredi', jsDay: 5 },
      { key: 'saturday', label: 'Samedi', jsDay: 6 },
      { key: 'sunday', label: 'Dimanche', jsDay: 0 },
    ];

    return days.map((d) => {
      const dayData = hours?.[d.key];
      const isToday = currentJsDay === d.jsDay;

      if (!dayData || !dayData.isOpen) {
        return {
          key: d.key,
          label: d.label,
          isOpen: false,
          isToday,
          statusText: 'Fermé',
          slotsText: '',
        };
      }

      if (dayData.byAppointmentOnly) {
        return {
          key: d.key,
          label: d.label,
          isOpen: true,
          isToday,
          statusText: 'Sur rendez-vous uniquement',
          slotsText: '',
        };
      }

      const slots: string[] = [];
      if (dayData.morningOpen && dayData.morningClose) {
        slots.push(`${dayData.morningOpen} - ${dayData.morningClose}`);
      }
      if (dayData.afternoonOpen && dayData.afternoonClose) {
        slots.push(`${dayData.afternoonOpen} - ${dayData.afternoonClose}`);
      }

      if (slots.length === 0) {
        return {
          key: d.key,
          label: d.label,
          isOpen: false,
          isToday,
          statusText: 'Fermé',
          slotsText: '',
        };
      }

      const slotsText = slots.join(' & ');

      return {
        key: d.key,
        label: d.label,
        isOpen: true,
        isToday,
        statusText: slotsText,
        slotsText,
      };
    });
  });

  readonly openingHoursStatus = computed(() => {
    const schedule = this.weekSchedule();
    const today = schedule.find((d) => d.isToday);
    if (!today || !today.isOpen) {
      return {
        isOpen: false,
        text: 'Fermé aujourd’hui',
        badgeClass: 'bg-neutral-2 text-neutral-10 border-neutral-3',
      };
    }
    if (today.statusText === 'Sur rendez-vous uniquement') {
      return {
        isOpen: true,
        isAppointment: true,
        text: 'Sur rendez-vous aujourd’hui',
        badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
      };
    }
    return {
      isOpen: true,
      text: `Ouvert aujourd’hui (${today.slotsText})`,
      badgeClass: 'bg-primary-2 text-primary-11 border-primary-4',
    };
  });

  getSpecialtyInfo(key: string) {
    return this.specialtyMeta[key] || { label: key, icon: 'category' };
  }

  getServiceInfo(key: string) {
    return this.serviceMeta[key] || { label: key, icon: 'check_circle' };
  }
}
